import React, { useEffect, useRef, useState } from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { DataService } from 'api/DataService'
import { useAuthContext } from 'context/AuthContext'
import {
  aggregatedScope,
  getRecordMerchant,
  isGroupMode,
  registerRecordMerchant,
  useActiveMerchant
} from 'lib/merchant-group'
import { Billing } from 'models/Overview/Transfer'
import { useRouter } from 'next/router'

import ModalChargeDetail from 'components/Modal/ModalChargeDetail'
import ModalRecurrenceDetail from 'components/Modal/ModalRecurrenceDetail'
import SearchModal from 'components/SearchModal'
import { ResultType } from 'components/SearchModal/SearchModal'

import { Debouncer, formatCpfCnpj, formatMoney } from 'utils'
import { CustomerData } from 'utils/mappers/customer'
import {
  addRecentItem,
  addRecentTerm,
  getRecentItems,
  getRecentTerms,
  RecentItem
} from 'utils/searchHistory'

import { theme } from 'styles/theme'

import AccountsPayableDetail from './AccountsPayableDetail'
import NfseDetail from './NfseDetail'
import { BigScreenButton } from './Search.styles'

// SC-10999: na busca da visão multi-empresas cada item traz a sua empresa
type SearchMerchant = {
  uuid: string
  name: string
  logo_url?: string | null
}

const merchantOf = (record: unknown): SearchMerchant | undefined =>
  (record as { merchant?: SearchMerchant } | undefined)?.merchant || undefined

type DetailModalState = {
  isOpen: boolean
  uuid: string
  // Empresa do registro (visão multi-empresas): o modal age com o token dela
  merchantUuid?: string
}

const closedModal: DetailModalState = { isOpen: false, uuid: '' }

// Rótulo com que a API indexa as anotações do Relacionamento no trecho casado
// (Customer::NOTE_SEARCH_TITLE no backend).
const NOTE_MATCH_PREFIX = 'Anotação:'

const isNoteMatch = (match?: string) =>
  !!match?.replace(/<\/?mark>/g, '').startsWith(NOTE_MATCH_PREFIX)

type SearchHighlight = {
  name?: string
  nickname?: string
  email?: string
}

type SearchAccountsPayable = {
  uuid: string
  description?: string
  amount_billed?: number | string
  status?: string
  due_date?: string
  accounts_payable_payee?: { name: string } | null
}

type SearchRecurrence = {
  uuid: string
  description?: string
  amount?: number | string
  status?: string
  customer?: { name: string } | null
}

type SearchProduct = {
  uuid: string
  name?: string
  description?: string
  amount?: number | string
  icon?: string | null
  thumbnail_url?: string | null
}

type SearchNfse = {
  uuid: string
  nfse_number?: string
  amount?: number | string
  status?: string
  customer_uuid?: string
  customer?: { name: string } | null
}

type SearchContract = {
  uuid: string
  title?: string
  description?: string
  status?: string
  customer?: { name: string } | null
}

export type Records = {
  billings?: Billing[]
  customers?: CustomerData[]
  accounts_payables?: SearchAccountsPayable[]
  recurrences?: SearchRecurrence[]
  products?: SearchProduct[]
  nfses?: SearchNfse[]
  contracts?: SearchContract[]
}

const formatDate = (date?: string) =>
  date ? date.split('-').reverse().join('/') : ''

const ACCOUNTS_PAYABLE_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  overdue: 'Vencida',
  paid: 'Paga',
  deleted: 'Deletada'
}

const accountsPayableStatusLabel = (status?: string) =>
  status ? ACCOUNTS_PAYABLE_STATUS_LABEL[status] : undefined

// Mesmos rótulos das telas de cada módulo (InvoiceTable.mapper e
// ListContracts.Table.utils), senão a busca chama o mesmo status de outro nome.
const NFSE_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  issued: 'Programada',
  confirmed: 'Emitida',
  canceled: 'Cancelada',
  failed: 'Falhada'
}

const CONTRACT_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  waiting_signatures: 'Aguardando assinaturas',
  current: 'Vigente',
  closed: 'Encerrado',
  expiring: 'Expirando',
  deleted: 'Excluído',
  no_signed: 'Expirado',
  error: 'Erro',
  draft: 'Rascunho',
  processing: 'Processando'
}

// Mostra o atalho conforme a plataforma (⌘K no Mac, Ctrl K nos demais).
const shortcutLabel = () => {
  if (typeof navigator === 'undefined') return 'Ctrl K'
  const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform)
  return isMac ? '⌘K' : 'Ctrl K'
}

const Search = () => {
  const { push } = useRouter()
  const { switchAccount } = useAuthContext()
  const [modalChargeProps, setModalChargeProps] =
    useState<DetailModalState>(closedModal)
  const [modalRecurrenceProps, setModalRecurrenceProps] =
    useState<DetailModalState>(closedModal)
  const [modalPayableProps, setModalPayableProps] =
    useState<DetailModalState>(closedModal)
  const [modalNfseProps, setModalNfseProps] =
    useState<DetailModalState>(closedModal)
  const [inputValue, setInputValue] = useState('')
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [results, setResuts] = useState<ResultType[]>([] as ResultType[])
  const [suggestion, setSuggestion] = useState<string | undefined>(undefined)
  const [recentTerms, setRecentTerms] = useState<string[]>([])
  const [recentItems, setRecentItems] = useState<RecentItem[]>([])
  const match = useMediaQuery('(max-width: 770px)', { noSsr: false })

  // Carrega o histórico (localStorage) sempre que a busca é aberta.
  useEffect(() => {
    if (modalIsOpen) {
      setRecentTerms(getRecentTerms())
      setRecentItems(getRecentItems())
    }
  }, [modalIsOpen])

  // Identifica a última busca disparada. Como o debounce não cancela requests
  // anteriores, uma resposta antiga (ex.: "igo") pode chegar depois da nova
  // ("igor") e sobrescrever os resultados certos. Descartamos respostas cujo id
  // não seja o do último request.
  const requestIdRef = useRef(0)

  // Termo da última busca com resultados. Só é salvo no histórico de recentes
  // quando o usuário abre algum resultado (não a cada tecla digitada).
  const lastQueryRef = useRef('')

  useEffect(() => {
    if (inputValue === '') {
      setResuts([] as ResultType[])
      setSuggestion(undefined)
    }
  }, [inputValue])

  const handleCloseSearchModal = () => {
    setModalIsOpen(false)
    setResuts([] as ResultType[])
    setSuggestion(undefined)
    setInputValue('')
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setInputValue(value)
    // Ativa o loading já na digitação (antes do debounce de 350ms disparar a
    // request). Sem isso, entre teclas o loading fica false com resultados
    // vazios e o chip "Tudo" + "nenhum resultado" piscam enquanto se digita.
    setLoading(value.trim() !== '')
    awaitForSubmit(value)
  }

  const handleClearInput = () => {
    setInputValue('')
    setResuts([] as ResultType[])
    setSuggestion(undefined)
  }

  // Registra a empresa do resultado antes de abrir o detalhe: o modal (e o que
  // ele abrir por dentro) busca e age com o token da empresa do registro. Os
  // recentes não guardam a empresa, então valem só os vistos nesta sessão.
  const openMerchantUuid = (uuid: string, merchant?: SearchMerchant) => {
    registerRecordMerchant(uuid, merchant?.uuid)
    return merchant?.uuid || getRecordMerchant(uuid)
  }

  // SC-10999: ficha de cliente e página de plano são por empresa. No grupo, abrir
  // um desses resultados troca para a empresa do registro (como a lista de
  // Clientes faz); o contrato abre com a empresa na URL, que a página aceita.
  const openRecordPage = (
    uuid: string,
    merchant: SearchMerchant | undefined,
    path: string
  ) => {
    const merchantUuid = openMerchantUuid(uuid, merchant)
    if (isGroupMode() && merchantUuid) {
      switchAccount({ merchantUuid }, { redirectTo: path })
      return
    }
    push(path)
  }

  const openContract = (uuid: string, merchant?: SearchMerchant) => {
    const merchantUuid = openMerchantUuid(uuid, merchant)
    push(
      isGroupMode() && merchantUuid
        ? `/contratos/${uuid}?merchant_uuid=${merchantUuid}`
        : `/contratos/${uuid}`
    )
  }

  useActiveMerchant(
    modalChargeProps.isOpen ? modalChargeProps.merchantUuid : undefined
  )
  useActiveMerchant(
    modalRecurrenceProps.isOpen ? modalRecurrenceProps.merchantUuid : undefined
  )
  useActiveMerchant(
    modalPayableProps.isOpen ? modalPayableProps.merchantUuid : undefined
  )
  useActiveMerchant(
    modalNfseProps.isOpen ? modalNfseProps.merchantUuid : undefined
  )

  const handleOpenDetailInvoice = (uuid: string, merchant?: SearchMerchant) => {
    const merchantUuid = openMerchantUuid(uuid, merchant)
    setModalChargeProps({ isOpen: true, uuid, merchantUuid })
    handleCloseSearchModal()
  }

  const handleCloseDetailInvoice = () => {
    setModalChargeProps(closedModal)
    handleCloseSearchModal()
  }

  const handleOpenDetailRecurrence = (
    uuid: string,
    merchant?: SearchMerchant
  ) => {
    const merchantUuid = openMerchantUuid(uuid, merchant)
    setModalRecurrenceProps({ isOpen: true, uuid, merchantUuid })
    handleCloseSearchModal()
  }

  const handleCloseDetailRecurrence = () => {
    setModalRecurrenceProps(closedModal)
    handleCloseSearchModal()
  }

  const handleOpenDetailPayable = (uuid: string, merchant?: SearchMerchant) => {
    const merchantUuid = openMerchantUuid(uuid, merchant)
    setModalPayableProps({ isOpen: true, uuid, merchantUuid })
    handleCloseSearchModal()
  }

  const handleCloseDetailPayable = () => {
    setModalPayableProps(closedModal)
  }

  const handleOpenDetailNfse = (uuid: string, merchant?: SearchMerchant) => {
    const merchantUuid = openMerchantUuid(uuid, merchant)
    setModalNfseProps({ isOpen: true, uuid, merchantUuid })
    handleCloseSearchModal()
  }

  const handleCloseDetailNfse = () => {
    setModalNfseProps(closedModal)
  }

  const handleSubmit = async (text: string) => {
    if (text.trim() === '') {
      setResuts([] as ResultType[])
      setSuggestion(undefined)
      setInputValue('')
      return
    }

    const currentRequestId = ++requestIdRef.current

    try {
      setLoading(true)
      const result = await DataService({
        data: { keywords: text },
        type: 'POST',
        url: '/v1/search',
        // SC-10999: na visão multi-empresas a busca cobre todas as empresas
        scope: aggregatedScope()
      })

      // Descarta resposta obsoleta: se outra busca foi disparada enquanto esta
      // estava em voo, o id mudou e não devemos sobrescrever os resultados novos.
      if (currentRequestId !== requestIdRef.current) return

      const data: Records = result.data.records
      // suggestions: termos sugeridos pelo Elasticsearch (searchkick suggest)
      // usados no "Você quis dizer...?" quando a busca não retorna resultados.
      const apiSuggestions: string[] = result.data.suggestions || []
      const {
        billings,
        customers,
        accounts_payables,
        recurrences,
        products,
        nfses,
        contracts
      } = data
      const results: ResultType[] = []

      customers?.forEach((customer) => {
        const typed = customer as CustomerData & {
          highlight?: SearchHighlight
          custom_field_match?: string
        }
        // SC-11227: achado pela anotação, o cliente abre na aba onde ela está.
        const customerPath = isNoteMatch(typed.custom_field_match)
          ? `/clientes/${customer.uuid}?tab=relacionamento`
          : `/clientes/${customer.uuid}`
        results.push({
          title: customer.name,
          titleHighlight: typed.highlight?.name,
          nickname: customer.nickname,
          category: customer.category,
          subtitle: `${formatCpfCnpj(customer.document)} | ${customer.email}`,
          // Quando o cliente casou por campo extra ou anotação, mostra
          // "título: valor" para o usuário entender por que ele apareceu na busca.
          customFieldMatch: typed.custom_field_match,
          type: 'client',
          // Navegação do cliente é feita pelo <Link> (clique) ou openResult
          // (Enter); o histórico de recentes é gravado em handleResultOpen.
          clickFunction: () =>
            openRecordPage(customer.uuid, merchantOf(customer), customerPath),
          href: customerPath,
          uuid: customer.uuid,
          merchant: merchantOf(customer)
        })
      })

      billings?.forEach((billing) => {
        results.push({
          title: billing.customer.name,
          status: billing.status,
          subtitle: `${formatMoney(
            Number(billing.amount_billed)
          )} | ${formatDate(billing.due_date)}`,
          type: 'billing',
          clickFunction: () => {
            handleOpenDetailInvoice(billing.uuid, merchantOf(billing))
          },
          dueDate: billing.due_date,
          uuid: billing.uuid,
          merchant: merchantOf(billing)
        })
      })

      accounts_payables?.forEach((payable) => {
        const payee = payable.accounts_payable_payee?.name
        const dueInfo = payable.due_date
          ? ` | Vence ${formatDate(payable.due_date)}`
          : ''
        results.push({
          title: payee || payable.description || 'Conta a pagar',
          statusLabel: accountsPayableStatusLabel(payable.status),
          subtitle: `${formatMoney(
            Number(payable.amount_billed || 0)
          )}${dueInfo}`,
          type: 'accounts_payable',
          clickFunction: () => {
            handleOpenDetailPayable(payable.uuid, merchantOf(payable))
          },
          uuid: payable.uuid,
          merchant: merchantOf(payable)
        })
      })

      recurrences?.forEach((recurrence) => {
        results.push({
          title:
            recurrence.customer?.name ||
            recurrence.description ||
            'Recorrência',
          status: recurrence.status,
          subtitle: formatMoney(Number(recurrence.amount || 0)),
          type: 'recurrence',
          clickFunction: () => {
            handleOpenDetailRecurrence(recurrence.uuid, merchantOf(recurrence))
          },
          uuid: recurrence.uuid,
          merchant: merchantOf(recurrence)
        })
      })

      products?.forEach((product) => {
        results.push({
          title: product.name || 'Produto',
          subtitle: formatMoney(Number(product.amount || 0)),
          type: 'product',
          productIcon: product.icon || undefined,
          productThumbnail: product.thumbnail_url || undefined,
          clickFunction: () =>
            openRecordPage(
              product.uuid,
              merchantOf(product),
              `/planos/${product.uuid}`
            ),
          uuid: product.uuid,
          merchant: merchantOf(product)
        })
      })

      nfses?.forEach((nfse) => {
        const customerName = nfse.customer?.name
        results.push({
          title: nfse.nfse_number
            ? `NF ${nfse.nfse_number}`
            : customerName || 'Nota fiscal',
          statusLabel: nfse.status ? NFSE_STATUS_LABEL[nfse.status] : undefined,
          subtitle: [
            customerName,
            nfse.amount ? formatMoney(Number(nfse.amount)) : undefined
          ]
            .filter(Boolean)
            .join(' | '),
          type: 'nfse',
          clickFunction: () => {
            handleOpenDetailNfse(nfse.uuid, merchantOf(nfse))
          },
          uuid: nfse.uuid,
          merchant: merchantOf(nfse)
        })
      })

      contracts?.forEach((contract) => {
        results.push({
          title: contract.title || contract.customer?.name || 'Contrato',
          statusLabel: contract.status
            ? CONTRACT_STATUS_LABEL[contract.status]
            : undefined,
          subtitle: contract.customer?.name || '',
          type: 'contract',
          clickFunction: () =>
            openContract(contract.uuid, merchantOf(contract)),
          uuid: contract.uuid,
          merchant: merchantOf(contract)
        })
      })

      setResuts([...results])
      // Guarda o termo da busca atual; só será salvo nos recentes quando o
      // usuário realmente abrir um resultado (evita poluir com parciais como
      // "ver" / "verme" / "vermelho" digitados durante a busca).
      lastQueryRef.current = text.trim()
      // Só oferece sugestão quando a busca não achou nada e a sugestão difere do
      // que o usuário digitou (evita "Você quis dizer 'igor'?" buscando "igor").
      const firstSuggestion = apiSuggestions.find(
        (s) => s && s.toLowerCase() !== text.trim().toLowerCase()
      )
      setSuggestion(results.length === 0 ? firstSuggestion : undefined)
      setLoading(false)
    } catch {
      if (currentRequestId !== requestIdRef.current) return
      setLoading(false)
    }
  }
  const awaitForSubmit = Debouncer(handleSubmit, 350)

  // Aplica uma sugestão do "Você quis dizer...?": preenche o input e refaz a busca.
  const handleApplySuggestion = (term: string) => {
    setInputValue(term)
    handleSubmit(term)
  }

  // Reabre um item do histórico de recentes, disparando a mesma ação por tipo
  // que o resultado da busca faria (navega ou abre o modal de detalhe).
  const handleRecentItemClick = (item: RecentItem) => {
    // SC-10999: o recente guarda a empresa; sem ela, vale a registrada na sessão
    const merchant = item.merchantUuid
      ? ({ uuid: item.merchantUuid } as SearchMerchant)
      : undefined
    switch (item.type) {
      case 'client':
        openRecordPage(item.uuid, merchant, `/clientes/${item.uuid}`)
        handleCloseSearchModal()
        break
      case 'product':
        openRecordPage(item.uuid, merchant, `/planos/${item.uuid}`)
        handleCloseSearchModal()
        break
      case 'contract':
        openContract(item.uuid, merchant)
        handleCloseSearchModal()
        break
      case 'nfse':
        handleOpenDetailNfse(item.uuid, merchant)
        break
      case 'billing':
        handleOpenDetailInvoice(item.uuid, merchant)
        break
      case 'recurrence':
        handleOpenDetailRecurrence(item.uuid, merchant)
        break
      case 'accounts_payable':
        handleOpenDetailPayable(item.uuid, merchant)
        break
    }
  }

  // Chamado quando o usuário abre QUALQUER resultado: aí sim a busca foi útil.
  // Grava o termo (recentes de busca) e o item acessado (recentes de item), de
  // qualquer tipo — cliente, cobrança, recorrência, plano, conta a pagar, etc.
  const handleResultOpen = (result: ResultType) => {
    if (lastQueryRef.current) addRecentTerm(lastQueryRef.current)
    if (result?.uuid) {
      addRecentItem({
        uuid: result.uuid,
        type: result.type,
        title: result.title,
        subtitle: result.subtitle,
        merchantUuid: (result as { merchant?: SearchMerchant }).merchant?.uuid
      })
    }
  }

  const handleKeyDown = (event: any) => {
    const isCtrlPressed = event.ctrlKey || event.metaKey
    if (!isCtrlPressed) return

    // Cmd/Ctrl+K: atalho padrão de mercado (Linear/Notion/Slack).
    // Ctrl+Espaço: mantido por compatibilidade com quem já usava.
    const isShortcut = event.key === ' ' || event.key?.toLowerCase() === 'k'
    if (isShortcut) {
      event.preventDefault()
      setModalIsOpen((prevState) => !prevState)
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false)
    return () => {
      document.removeEventListener('keydown', handleKeyDown, false)
    }
  }, [])

  return (
    <>
      <Tooltip enterTouchDelay={0} title={`Buscar (${shortcutLabel()})`}>
        {match ? (
          <IconButton onClick={() => setModalIsOpen(true)}>
            <Icon icon="search" style={{ color: '#777877' }} />
          </IconButton>
        ) : (
          <BigScreenButton onClick={() => setModalIsOpen(true)}>
            <Icon
              icon="search"
              style={{ color: theme.palette.neutral[400], fontSize: '24px' }}
              fontSize="large"
            />
            <Typography
              variant="xsLight"
              fontSize={10}
              color={theme.palette.neutral[400]}
            >
              Buscar
            </Typography>
          </BigScreenButton>
        )}
      </Tooltip>

      <SearchModal
        isOpen={modalIsOpen}
        handleCloseFunction={handleCloseSearchModal}
        placeholder="Buscar na sua Base de Clientes"
        inputValue={inputValue}
        handleInputChangeFunction={handleInputChange}
        clearInputFunction={handleClearInput}
        results={results}
        loadingResults={loading}
        suggestion={suggestion}
        onApplySuggestion={handleApplySuggestion}
        onResultOpen={handleResultOpen}
        recentTerms={recentTerms}
        recentItems={recentItems}
        onApplyRecentTerm={handleApplySuggestion}
        onRecentItemClick={handleRecentItemClick}
      />

      <ModalChargeDetail
        disablePortal={false}
        handleCloseChargeDetail={handleCloseDetailInvoice}
        isOpenChargeDetail={modalChargeProps.isOpen}
        uuid={modalChargeProps.uuid}
      />

      <ModalRecurrenceDetail
        uuid={modalRecurrenceProps.uuid}
        isOpenModalRecurrenceDetail={modalRecurrenceProps.isOpen}
        handleCloseDetailRecurrence={handleCloseDetailRecurrence}
      />

      <AccountsPayableDetail
        isOpen={modalPayableProps.isOpen}
        uuid={modalPayableProps.uuid}
        onClose={handleCloseDetailPayable}
      />

      <NfseDetail
        isOpen={modalNfseProps.isOpen}
        uuid={modalNfseProps.uuid}
        onClose={handleCloseDetailNfse}
      />
    </>
  )
}

export default Search
